import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Column } from 'write-excel-file/browser';
import { Client, ClientQueryFilters, clientsAPI, usersAPI, User } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  UserIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  UsersIcon,
  UserPlusIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  StarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import AdvancedSearchModal from '../search/AdvancedSearchModal';
import {
  Card,
  CardBody,
  Button,
  Badge,
  Avatar,
  EmptyState,
  Skeleton,
  SkeletonCard,
  Modal,
  SearchInput,
  PageHero,
  Spinner,
} from '../ui';

const getAllContactEmails = (client: Client) =>
  Array.from(new Set([client.email, ...(client.additionalEmails || [])]
    .map(email => email?.trim())
    .filter((email): email is string => Boolean(email))))
    .join('; ');

const getAllContactPhones = (client: Client) =>
  Array.from(new Set(client.phoneNumbers
    .map(phone => phone?.trim())
    .filter((phone): phone is string => Boolean(phone))))
    .join('; ');

const ClientsList: React.FC = () => {
  const EXPORT_BATCH_SIZE = 1000;
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // Separate state for search input
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table'); // New: View mode toggle
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false); // Phase 3: Advanced Search
  const [pageView, setPageView] = useState<'active' | 'deleted'>('active'); // Deleted folder toggle
  const [deletedClients, setDeletedClients] = useState<any[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    importance: '',
    assignedUserId: '',
    lifecycleStage: '', // Phase 1.1
    leadStatus: '', // Phase 1.1
  });

  // Phase 5.1: New state for enhancements
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [visibleColumns, setVisibleColumns] = useState({
    contact: true,
    importance: true,
    assignedUser: true,
    pipelineStage: true, // Pipeline stage column
    lifecycleStage: true,
    leadStatus: true,
    leadScore: true, // Lead score column
    deals: true,
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [exporting, setExporting] = useState(false);

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getClientPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };

  useEffect(() => {
    fetchClients();
    if (user?.role === 'ADMIN') {
      fetchUsers();
    }
  }, []);

  // Debounce search input - wait 1.5 seconds after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }, 1500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    fetchClients();
  }, [pagination.page, filters]);

  const fetchDeletedClients = async () => {
    setDeletedLoading(true);
    try {
      const data = await clientsAPI.getDeleted();
      setDeletedClients(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deleted clients');
    } finally {
      setDeletedLoading(false);
    }
  };

  useEffect(() => {
    if (pageView === 'deleted') fetchDeletedClients();
  }, [pageView]);

  const handleRestore = async (id: number) => {
    setRestoringId(id);
    try {
      await clientsAPI.restore(id);
      fetchDeletedClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore client');
    } finally {
      setRestoringId(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await usersAPI.getSalesmen();
      setUsers(data);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await clientsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type || undefined,
        importance: filters.importance || undefined,
        assignedUserId: filters.assignedUserId ? parseInt(filters.assignedUserId) : undefined,
        lifecycleStage: filters.lifecycleStage || undefined,
        leadStatus: filters.leadStatus || undefined,
      });
      setClients(data.data);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await clientsAPI.delete(id);
      setClients(clients.filter(client => client.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete client');
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'search') {
      // For search, just update the input state - debounce will handle the filter update
      setSearchInput(value);
    } else {
      // For other filters, update immediately
      setFilters(prev => ({ ...prev, [key]: value }));
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setFilters({ search: '', type: '', importance: '', assignedUserId: '', lifecycleStage: '', leadStatus: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Phase 5.1: Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const handleSelectClient = (clientId: number) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Phase 5.1: Remove filter chip
  const removeFilterChip = (filterKey: string) => {
    setFilters(prev => ({ ...prev, [filterKey]: '' }));
    if (filterKey === 'search') {
      setSearchInput('');
    }
  };

  // Phase 5.1: Export to Excel
  const getActiveClientFilters = (): ClientQueryFilters => ({
    search: filters.search || undefined,
    type: filters.type || undefined,
    importance: filters.importance || undefined,
    assignedUserId: filters.assignedUserId ? parseInt(filters.assignedUserId) : undefined,
    lifecycleStage: filters.lifecycleStage || undefined,
    leadStatus: filters.leadStatus || undefined,
  });

  const fetchAllFilteredClients = async () => {
    const allClients: Client[] = [];
    let exportPage = 1;
    let exportPages = 1;

    while (exportPage <= exportPages) {
      const response = await clientsAPI.getAll({
        ...getActiveClientFilters(),
        page: exportPage,
        limit: EXPORT_BATCH_SIZE,
      });
      allClients.push(...response.data);
      exportPages = response.pagination.pages;
      exportPage += 1;
    }

    return allClients;
  };

  const handleExportExcel = async (selectedOnly = false) => {
    try {
      setExporting(true);
      setError('');

      const filteredClients = await fetchAllFilteredClients();
      const clientsToExport = selectedOnly
        ? filteredClients.filter(client => selectedClients.includes(client.id))
        : filteredClients;

      if (clientsToExport.length === 0) {
        setError(selectedOnly ? 'No selected clients to export' : 'No clients to export');
        return;
      }

      const header = (value: string) => ({
        value,
        fontWeight: 'bold' as const,
        backgroundColor: '#DBEAFE',
        align: 'center' as const,
      });
      const columns: Column<Client>[] = [
        { header: header('Name'), width: 30, cell: client => client.name },
        { header: header('Type'), width: 18, cell: client => client.type },
        { header: header('Emails'), width: 45, cell: getAllContactEmails },
        { header: header('Phone Numbers'), width: 32, cell: getAllContactPhones },
        { header: header('Importance'), width: 16, cell: client => client.importance },
        { header: header('Lifecycle Stage'), width: 20, cell: client => client.lifecycleStage || '' },
        { header: header('Lead Status'), width: 22, cell: client => client.leadStatus || '' },
        {
          header: header('Assigned To'),
          width: 26,
          cell: client => client.assignedUser
            ? `${client.assignedUser.firstName || ''} ${client.assignedUser.lastName || ''}`.trim()
            : '',
        },
      ];
      const { default: writeExcelFile } = await import('write-excel-file/browser');
      const date = new Date().toISOString().split('T')[0];

      await writeExcelFile(clientsToExport, {
        columns,
        sheet: 'Clients',
        stickyRowsCount: 1,
      }).toFile(`clients_${date}.xlsx`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export clients');
    } finally {
      setExporting(false);
    }
  };

  // Phase 5.1: Bulk Delete
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedClients.length} client(s)?`)) {
      return;
    }

    try {
      await Promise.all(selectedClients.map(id => clientsAPI.delete(id)));
      setClients(clients.filter(c => !selectedClients.includes(c.id)));
      setSelectedClients([]);
      fetchClients(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete clients');
    }
  };

  // Phase 5.1: Sorting
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to clients
  const sortedClients = React.useMemo(() => {
    if (!sortConfig) return clients;

    const sorted = [...clients].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Client];
      const bValue = b[sortConfig.key as keyof Client];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }, [clients, sortConfig]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Phase 5.1: Sort icon helper
  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return (
        <svg className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-primary-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-primary-600" />
    );
  };

  const getClientTypeIcon = (type: string) => {
    switch (type) {
      case 'COMPANY':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'STORE':
        return <BuildingStorefrontIcon className="h-5 w-5" />;
      case 'PERSON':
        return <UserIcon className="h-5 w-5" />;
      case 'ORGANIZATION':
        return <GlobeAltIcon className="h-5 w-5" />;
      default:
        return <BuildingOfficeIcon className="h-5 w-5" />;
    }
  };

  const getImportanceBadge = (importance: string) => {
    const variants: Record<string, 'neutral' | 'primary' | 'warning' | 'error' | 'success'> = {
      VERY_LOW: 'neutral',
      LOW: 'primary',
      MEDIUM: 'warning',
      HIGH: 'error',
      VERY_HIGH: 'error',
    };

    return (
      <Badge variant={variants[importance] || 'neutral'} size="sm">
        {importance.replace('_', ' ')}
      </Badge>
    );
  };

  const getClientStatusBadge = (client: Client) => {
    if (!user) return null;

    const isCreatedByMe = client.createdById === user.id;
    const isAssignedToMe = client.assignedUserId === user.id;

    if (isCreatedByMe && isAssignedToMe) {
      return (
        <Badge variant="success" size="sm">
          <UserIcon className="h-3 w-3 mr-1" />
          Created & Assigned
        </Badge>
      );
    } else if (isCreatedByMe) {
      return (
        <Badge variant="primary" size="sm">
          <UserIcon className="h-3 w-3 mr-1" />
          Created by me
        </Badge>
      );
    } else if (isAssignedToMe) {
      return (
        <Badge variant="warning" size="sm">
          <UserPlusIcon className="h-3 w-3 mr-1" />
          Assigned to me
        </Badge>
      );
    }

    return null;
  };

  // Enhanced loading skeleton
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Skeleton variant="text" className="w-32 h-8 mb-2" />
            <Skeleton variant="text" className="w-64 h-4" />
          </div>
          <div className="flex gap-3">
            <Skeleton variant="rectangular" className="w-32 h-10 rounded-lg" />
            <Skeleton variant="rectangular" className="w-28 h-10 rounded-lg" />
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-tour="page-content">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Clients"
        subtitle={user?.role === 'ADMIN'
          ? 'Manage all clients and their information'
          : 'Your assigned clients and contacts'}
        icon={<UsersIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="blue"
      >
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {/* Deleted folder toggle (admin only) */}
          {user?.role === 'ADMIN' && (
            <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
              <button
                onClick={() => setPageView('active')}
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  pageView === 'active' ? 'bg-primary-600 text-white shadow-sm' : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setPageView('deleted')}
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  pageView === 'deleted' ? 'bg-error-600 text-white shadow-sm' : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Deleted
              </button>
            </div>
          )}
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'table'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 hover:bg-neutral-50'
                }`}
            >
              <TableCellsIcon className="h-4 w-4 mr-2" />
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'cards'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 hover:bg-neutral-50'
                }`}
            >
              <Squares2X2Icon className="h-4 w-4 mr-2" />
              Cards
            </button>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowAdvancedSearch(true)}
            leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          >
            Advanced Search
          </Button>

          <Link to={getClientPath("/clients/new")}>
            <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
              Add Client
            </Button>
          </Link>
        </div>
      </PageHero>

      {/* Deleted view */}
      {pageView === 'deleted' && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-neutral-700 mb-4">
            Deleted Clients {deletedClients.length > 0 && `(${deletedClients.length})`}
          </h3>
          {deletedLoading ? (
            <div className="flex justify-center py-12"><Spinner className="h-8 w-8" /></div>
          ) : deletedClients.length === 0 ? (
            <EmptyState
              icon={<TrashIcon className="h-12 w-12" />}
              title="No deleted clients"
              description="Clients you delete will appear here and can be restored."
            />
          ) : (
            <div className="space-y-3">
              {deletedClients.map((client) => (
                <Card key={client.id} variant="bordered" className="border-neutral-200 opacity-80">
                  <CardBody className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-neutral-800">{client.name}</p>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          {client.type}{client.email ? ` · ${client.email}` : ''}
                          {client.deletedAt && (
                            <> · Deleted {new Date(client.deletedAt).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRestore(client.id)}
                        isLoading={restoringId === client.id}
                        leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                      >
                        Restore
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {pageView === 'active' && (
      <>
      {/* Error Alert */}
      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50 mb-6">
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error-100 flex items-center justify-center">
                <XMarkIcon className="h-5 w-5 text-error-600" />
              </div>
              <div className="text-sm text-error-700">{error}</div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Filters Section */}
      <div className="mb-6">
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          leftIcon={<FunnelIcon className="h-5 w-5" />}
          rightIcon={
            Object.values(filters).some(v => v) ? (
              <Badge variant="primary" size="sm">Active</Badge>
            ) : undefined
          }
        >
          Filters
        </Button>

        {showFilters && (
          <Card variant="bordered" className="mt-4">
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* Search Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Search</label>
                  <SearchInput
                    value={searchInput}
                    onChange={(value) => handleFilterChange('search', value)}
                    placeholder="Name, email, phone..."
                    onClear={() => handleFilterChange('search', '')}
                  />
                </div>

                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Type</label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Types</option>
                    <option value="COMPANY">Company</option>
                    <option value="STORE">Store</option>
                    <option value="PERSON">Person</option>
                    <option value="ORGANIZATION">Organization</option>
                  </select>
                </div>

                {/* Importance Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Importance</label>
                  <select
                    value={filters.importance}
                    onChange={(e) => handleFilterChange('importance', e.target.value)}
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Levels</option>
                    <option value="VERY_LOW">Very Low</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="VERY_HIGH">Very High</option>
                  </select>
                </div>

                {/* Assigned User Filter (Admin only) */}
                {user?.role === 'ADMIN' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Assigned To</label>
                    <select
                      value={filters.assignedUserId}
                      onChange={(e) => handleFilterChange('assignedUserId', e.target.value)}
                      className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="">All Users</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Lifecycle Stage Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Lifecycle Stage</label>
                  <select
                    value={filters.lifecycleStage}
                    onChange={(e) => handleFilterChange('lifecycleStage', e.target.value)}
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Stages</option>
                    <option value="SUBSCRIBER">Subscriber</option>
                    <option value="LEAD">Lead</option>
                    <option value="MQL">Marketing Qualified Lead</option>
                    <option value="SQL">Sales Qualified Lead</option>
                    <option value="OPPORTUNITY">Opportunity</option>
                    <option value="CUSTOMER">Customer</option>
                    <option value="EVANGELIST">Evangelist</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* Lead Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Lead Status</label>
                  <select
                    value={filters.leadStatus}
                    onChange={(e) => handleFilterChange('leadStatus', e.target.value)}
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Statuses</option>
                    <option value="NEW">New</option>
                    <option value="ATTEMPTING_TO_CONTACT">Attempting to Contact</option>
                    <option value="CONNECTED">Connected</option>
                    <option value="OPEN_DEAL">Open Deal</option>
                    <option value="UNQUALIFIED">Unqualified</option>
                    <option value="BAD_TIMING">Bad Timing</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {Object.values(filters).some(v => v) && (
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    leftIcon={<XMarkIcon className="h-4 w-4" />}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {/* Quick Filter Chips */}
      {Object.entries(filters).some(([key, value]) => value) && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-neutral-600">Active Filters:</span>
          {filters.search && (
            <Badge variant="primary" size="sm" className="gap-1">
              Search: "{filters.search}"
              <button
                onClick={() => removeFilterChip('search')}
                className="ml-1 hover:bg-primary-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.type && (
            <Badge variant="secondary" size="sm" className="gap-1">
              Type: {filters.type}
              <button
                onClick={() => removeFilterChip('type')}
                className="ml-1 hover:bg-secondary-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.importance && (
            <Badge variant="warning" size="sm" className="gap-1">
              Importance: {filters.importance.replace('_', ' ')}
              <button
                onClick={() => removeFilterChip('importance')}
                className="ml-1 hover:bg-warning-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.lifecycleStage && (
            <Badge variant="success" size="sm" className="gap-1">
              Stage: {filters.lifecycleStage}
              <button
                onClick={() => removeFilterChip('lifecycleStage')}
                className="ml-1 hover:bg-success-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.leadStatus && (
            <Badge variant="primary" size="sm" className="gap-1">
              Status: {filters.leadStatus.replace(/_/g, ' ')}
              <button
                onClick={() => removeFilterChip('leadStatus')}
                className="ml-1 hover:bg-primary-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.assignedUserId && (
            <Badge variant="secondary" size="sm" className="gap-1">
              Assigned: {users.find(u => u.id === parseInt(filters.assignedUserId))?.firstName || 'User'}
              <button
                onClick={() => removeFilterChip('assignedUserId')}
                className="ml-1 hover:bg-secondary-200 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            leftIcon={<XMarkIcon className="h-4 w-4" />}
          >
            Clear All
          </Button>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedClients.length > 0 && (
        <Card variant="bordered" className="mb-6 border-primary-200 bg-primary-50/50">
          <CardBody className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="primary" size="md">
                  {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedClients([])}
                >
                  Clear selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {isAdminContext && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleExportExcel(true)}
                    isLoading={exporting}
                    disabled={exporting}
                    leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  >
                    Export Selected
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  leftIcon={<TrashIcon className="h-4 w-4" />}
                >
                  Delete ({selectedClients.length})
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card variant="elevated" className="overflow-hidden">
          {/* Table Header */}
          <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                All Clients ({pagination.total})
              </h3>
              <div className="flex items-center gap-3">
                {/* Export Button */}
                {isAdminContext && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleExportExcel()}
                    isLoading={exporting}
                    disabled={loading || exporting || pagination.total === 0}
                    leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  >
                    Export Excel
                  </Button>
                )}

                {/* Column Visibility Toggle */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumnMenu(!showColumnMenu)}
                    leftIcon={<TableCellsIcon className="h-4 w-4" />}
                  >
                    Columns
                  </Button>
                  {showColumnMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowColumnMenu(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-neutral-200 py-2 z-20">
                        <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase">Show/Hide Columns</div>
                        {Object.entries(visibleColumns).map(([key, value]) => (
                          <label key={key} className="flex items-center px-3 py-2 hover:bg-neutral-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => setVisibleColumns(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                            />
                            <span className="ml-2 text-sm text-neutral-700 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <span className="text-xs text-neutral-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  {/* Bulk Selection Checkbox */}
                  <th scope="col" className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedClients.length === clients.length && clients.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded cursor-pointer"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Client</span>
                      {getSortIcon('name')}
                    </div>
                  </th>
                  {visibleColumns.contact && (
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                      Contact Info
                    </th>
                  )}
                  {visibleColumns.assignedUser && user?.role === 'ADMIN' && (
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                      Assigned To
                    </th>
                  )}
                  {visibleColumns.pipelineStage && (
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                      Pipeline Stage
                    </th>
                  )}
                  {visibleColumns.lifecycleStage && (
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors"
                      onClick={() => handleSort('lifecycleStage')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Lifecycle</span>
                        {getSortIcon('lifecycleStage')}
                      </div>
                    </th>
                  )}
                  {visibleColumns.leadStatus && (
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors"
                      onClick={() => handleSort('leadStatus')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Lead Status</span>
                        {getSortIcon('leadStatus')}
                      </div>
                    </th>
                  )}
                  {visibleColumns.importance && (
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors"
                      onClick={() => handleSort('importance')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Importance</span>
                        {getSortIcon('importance')}
                      </div>
                    </th>
                  )}
                  {visibleColumns.leadScore && (
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors"
                      onClick={() => handleSort('leadScore')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Lead Score</span>
                        {getSortIcon('leadScore')}
                      </div>
                    </th>
                  )}
                  {visibleColumns.deals && (
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                      Deals
                    </th>
                  )}
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-100">
                {sortedClients.map((client, index) => (
                  <tr
                    key={client.id}
                    onClick={(e) => {
                      // Don't navigate if clicking on checkbox, link, or button
                      const target = e.target as HTMLElement;
                      if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('a') || target.closest('button')) {
                        return;
                      }
                      navigate(getClientPath(`/clients/${client.id}`));
                    }}
                    className={`hover:bg-primary-50/50 transition-all duration-200 group cursor-pointer ${selectedClients.includes(client.id) ? 'bg-primary-50' : ''
                      }`}
                  >
                    {/* Checkbox Column */}
                    <td className="px-6 py-5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center">
                        <Avatar
                          name={client.name}
                          size="md"
                          className="bg-gradient-to-br from-primary-100 to-primary-200"
                        />
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-neutral-900 group-hover:text-primary-700 transition-colors duration-200">
                            {client.name}
                          </div>
                          <div className="text-xs text-neutral-500 capitalize mt-0.5 flex items-center">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-400 mr-1.5"></span>
                            {client.type.toLowerCase()}
                          </div>
                        </div>
                      </div>
                    </td>
                    {visibleColumns.contact && (
                      <td className="px-6 py-5">
                        <div className="space-y-1">
                          <div className="text-sm text-neutral-900 flex items-center">
                            <EnvelopeIcon className="w-4 h-4 mr-2 text-neutral-400" />
                            {client.email || <span className="text-neutral-400 italic">No email</span>}
                          </div>
                          <div className="text-sm text-neutral-600 flex items-center">
                            <PhoneIcon className="w-4 h-4 mr-2 text-neutral-400" />
                            {client.phoneNumbers.length > 0 ? client.phoneNumbers[0] : <span className="text-neutral-400 italic">No phone</span>}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.assignedUser && user?.role === 'ADMIN' && (
                      <td className="px-6 py-5">
                        {client.assignedUser ? (
                          <div className="flex items-center">
                            <Avatar
                              name={`${client.assignedUser.firstName} ${client.assignedUser.lastName}`}
                              size="sm"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-neutral-900">
                                {client.assignedUser.firstName} {client.assignedUser.lastName}
                              </div>
                              <div className="text-xs text-neutral-500">{client.assignedUser.email}</div>
                            </div>
                          </div>
                        ) : (
                          <Badge variant="neutral" size="sm">Unassigned</Badge>
                        )}
                      </td>
                    )}
                    {visibleColumns.pipelineStage && (
                      <td className="px-6 py-5">
                        {client.stage ? (
                          <div className="flex items-center space-x-2">
                            {client.stage.color && (
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: client.stage.color }}
                              />
                            )}
                            <span className="text-sm font-medium text-neutral-900">
                              {client.stage.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-400">Not assigned</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.lifecycleStage && (
                      <td className="px-6 py-5">
                        <Badge variant="primary" size="sm">
                          {client.lifecycleStage ? client.lifecycleStage.replace('_', ' ') : 'LEAD'}
                        </Badge>
                      </td>
                    )}
                    {visibleColumns.leadStatus && (
                      <td className="px-6 py-5">
                        <Badge variant="success" size="sm">
                          {client.leadStatus ? client.leadStatus.replace('_', ' ') : 'NEW'}
                        </Badge>
                      </td>
                    )}
                    {visibleColumns.importance && (
                      <td className="px-6 py-5">
                        {getImportanceBadge(client.importance)}
                      </td>
                    )}
                    {visibleColumns.leadScore && (
                      <td className="px-6 py-5">
                        <div className="flex items-center">
                          <Badge
                            variant={
                              (client.leadScore || 0) >= 80 ? 'success' :
                                (client.leadScore || 0) >= 60 ? 'primary' :
                                  (client.leadScore || 0) >= 40 ? 'warning' :
                                    'neutral'
                            }
                            size="sm"
                          >
                            <StarIcon className="h-3 w-3 mr-1" />
                            {client.leadScore || 0}
                          </Badge>
                        </div>
                      </td>
                    )}
                    {visibleColumns.deals && (
                      <td className="px-6 py-5 whitespace-nowrap">
                        <Badge variant="secondary" size="sm" className="whitespace-nowrap">
                          {client._count?.deals || 0} deals
                        </Badge>
                      </td>
                    )}
                    <td className="px-6 py-5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end space-x-2">
                        <Link to={getClientPath(`/clients/${client.id}`)}>
                          <Button variant="ghost" size="sm" title="View Client">
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={getClientPath(`/clients/${client.id}/edit`)}>
                          <Button variant="ghost" size="sm" title="Edit Client">
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(client.id)}
                          className="text-error-600 hover:bg-error-50"
                          title="Delete Client"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {clients.length === 0 && (
              <EmptyState
                type="clients"
                title="No clients found"
                description="Get started by creating your first client."
                action={
                  <Link to={getClientPath("/clients/new")}>
                    <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
                      New Client
                    </Button>
                  </Link>
                }
              />
            )}
          </div>
        </Card>
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <div>
          {clients.length === 0 ? (
            <Card variant="bordered">
              <CardBody>
                <EmptyState
                  type="clients"
                  title="No clients found"
                  description="Get started by creating your first client."
                  action={
                    <Link to={getClientPath("/clients/new")}>
                      <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
                        New Client
                      </Button>
                    </Link>
                  }
                />
              </CardBody>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clients.map((client) => (
                <Card
                  key={client.id}
                  variant="elevated"
                  hoverable
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.tagName === 'BUTTON' || target.closest('a') || target.closest('button')) {
                      return;
                    }
                    navigate(getClientPath(`/clients/${client.id}`));
                  }}
                  className="cursor-pointer overflow-hidden"
                >
                  {/* Card Header with gradient */}
                  <div className="h-1.5 bg-gradient-to-r from-primary-500 to-primary-700"></div>

                  <CardBody className="p-5">
                    {/* Client Info */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3 flex-1">
                        <Avatar
                          name={client.name}
                          size="md"
                          className="bg-gradient-to-br from-neutral-100 to-neutral-200 group-hover:from-primary-100 group-hover:to-primary-200"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-neutral-900 truncate group-hover:text-primary-700 transition-colors">
                            {client.name}
                          </h3>
                          <p className="text-sm text-neutral-500 capitalize">{client.type.toLowerCase()}</p>
                        </div>
                      </div>
                      {user?.role !== 'ADMIN' && getClientStatusBadge(client)}
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-neutral-600">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-neutral-400" />
                        <span className="truncate">{client.email || 'No email'}</span>
                      </div>
                      <div className="flex items-center text-sm text-neutral-600">
                        <PhoneIcon className="h-4 w-4 mr-2 text-neutral-400" />
                        <span>{client.phoneNumbers.length > 0 ? client.phoneNumbers[0] : 'No phone'}</span>
                      </div>
                      {client.address && (
                        <div className="flex items-start text-sm text-neutral-600">
                          <MapPinIcon className="h-4 w-4 mr-2 text-neutral-400 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{client.address}</span>
                        </div>
                      )}
                    </div>

                    {/* Pipeline Stage */}
                    {client.stage && (
                      <div className="flex items-center space-x-2 mb-3 px-3 py-2 bg-neutral-50 rounded-lg">
                        {client.stage.color && (
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: client.stage.color }}
                          />
                        )}
                        <span className="text-sm font-medium text-neutral-700">
                          {client.stage.name}
                        </span>
                        {client.pipeline && (
                          <span className="text-xs text-neutral-500">
                            ({client.pipeline.name})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="primary" size="sm">
                        {client.lifecycleStage ? client.lifecycleStage.replace('_', ' ') : 'LEAD'}
                      </Badge>
                      <Badge variant="success" size="sm">
                        {client.leadStatus ? client.leadStatus.replace('_', ' ') : 'NEW'}
                      </Badge>
                      {getImportanceBadge(client.importance)}
                    </div>

                    {/* Assigned User, Lead Score & Deals */}
                    <div className="flex items-center justify-between text-sm text-neutral-600 mb-4 pb-4 border-b border-neutral-200">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1.5 text-neutral-400" />
                        <span className="truncate">
                          {client.assignedUser
                            ? `${client.assignedUser.firstName} ${client.assignedUser.lastName}`
                            : 'Unassigned'
                          }
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        {/* Lead Score */}
                        <Badge
                          variant={
                            (client.leadScore || 0) >= 80 ? 'success' :
                              (client.leadScore || 0) >= 60 ? 'primary' :
                                (client.leadScore || 0) >= 40 ? 'warning' :
                                  'neutral'
                          }
                          size="sm"
                        >
                          <StarIcon className="h-3 w-3 mr-1" />
                          {client.leadScore || 0}
                        </Badge>
                        {/* Deals */}
                        <Badge variant="secondary" size="sm">
                          {client._count?.deals || 0} deals
                        </Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Link to={getClientPath(`/clients/${client.id}`)} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full" leftIcon={<EyeIcon className="h-4 w-4" />}>
                          View
                        </Button>
                      </Link>
                      <Link to={getClientPath(`/clients/${client.id}/edit`)}>
                        <Button variant="ghost" size="sm">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(client.id)}
                        className="text-error-600 hover:bg-error-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {pagination.pages > 1 && (
        <Card variant="bordered" className="mt-6">
          <CardBody className="py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-600">
                Showing <span className="font-semibold text-primary-600">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-primary-600">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-primary-600">{pagination.total}</span> results
              </div>
              <div className="flex items-center gap-2 overflow-x-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </Button>

                {/* Page numbers with smart pagination */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    const halfVisible = Math.floor(maxVisible / 2);
                    let startPage = Math.max(1, pagination.page - halfVisible);
                    let endPage = Math.min(pagination.pages, startPage + maxVisible - 1);

                    if (endPage - startPage + 1 < maxVisible) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <Button key={1} variant="outline" size="sm" onClick={() => handlePageChange(1)}>
                          1
                        </Button>
                      );
                    }

                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="px-2 text-neutral-500">...</span>
                      );
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={i === pagination.page ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>
                      );
                    }

                    if (endPage < pagination.pages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="px-2 text-neutral-500">...</span>
                      );
                    }

                    if (endPage < pagination.pages) {
                      pages.push(
                        <Button
                          key={pagination.pages}
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.pages)}
                        >
                          {pagination.pages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Client"
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-error-100 mb-4">
            <TrashIcon className="h-7 w-7 text-error-600" />
          </div>
          <p className="text-sm text-neutral-600 mb-6">
            Are you sure you want to delete this client? This action cannot be undone and will permanently remove all associated data.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirm!)}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Advanced Search Modal */}
      <AdvancedSearchModal
        isOpen={showAdvancedSearch}
        onClose={() => setShowAdvancedSearch(false)}
        onSearch={(criteria) => {
          setFilters({
            search: criteria.search || '',
            type: criteria.type || '',
            importance: criteria.importance || '',
            assignedUserId: criteria.assignedUserId || '',
            lifecycleStage: criteria.lifecycleStage || '',
            leadStatus: criteria.leadStatus || '',
          });
          setPagination({ ...pagination, page: 1 });
        }}
        initialCriteria={filters}
      />
      </>
      )}
    </div>
  );
};

export default ClientsList;
